import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ShieldCheck } from "lucide-react";
import { BUSINESS, SERVICES, SERVICE_CATEGORIES } from "../data/business";
import useCloseOnRouteChange from "../hooks/useCloseOnRouteChange";
import QuoteCTA from "./QuoteCTA";

const NAV_LINKS = [
  { to: "/reviews", label: "Reviews", testid: "nav-reviews" },
  { to: "/faq", label: "FAQs", testid: "nav-faqs" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const MOBILE_NAV_ITEMS = [
  { to: "/", label: "Home", testid: "mnav-home" },
  { to: "/services", label: "Services", testid: "mnav-services" },
  { to: "/about", label: "About", testid: "mnav-about" },
  { to: "/reviews", label: "Reviews", testid: "mnav-reviews" },
  { to: "/faq", label: "FAQs", testid: "mnav-faqs" },
  { to: "/contact", label: "Contact", testid: "mnav-contact" },
  { to: "/service-areas", label: "Service Areas", testid: "mnav-areas" },
];

// Time (ms) the dropdown waits before closing after the cursor leaves the trigger.
// Long enough for the mouse to cross the visual gap into the panel without flicker.
const DROPDOWN_CLOSE_DELAY = 160;

// SERVICES is a module constant — group once per module load.
const SERVICES_BY_CATEGORY = SERVICE_CATEGORIES.reduce((acc, cat) => {
  acc[cat] = SERVICES.filter((s) => s.category === cat);
  return acc;
}, {});

// Shared hook: open immediately on enter, close after a short delay on leave so
// the mouse can travel from the trigger to the panel without losing focus.
function useHoverDropdown() {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const cancelClose = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const handleEnter = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);
  const handleLeave = useCallback(() => {
    cancelClose();
    timerRef.current = setTimeout(() => setOpen(false), DROPDOWN_CLOSE_DELAY);
  }, [cancelClose]);
  const closeNow = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);
  useCloseOnRouteChange(closeNow);
  useEffect(() => () => cancelClose(), [cancelClose]);
  return { open, handleEnter, handleLeave, closeNow };
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-home-link">
      <img
        src="/drain-sights-logo.png"
        alt="Drain Sights logo"
        className="w-10 h-10 rounded-xl object-cover shadow-md group-hover:scale-110 transition-transform duration-200"
      />
      <span className="font-display text-base sm:text-lg font-extrabold text-[#1A1D21] tracking-tight leading-tight">
        Drain <span className="text-[#E31D2B]">Sights</span>
      </span>
    </Link>
  );
}

function MegaPanel({ onEnter, onLeave }) {
  return (
    // The outer wrapper handles ABSOLUTE positioning & horizontal centering
    // (`-translate-x-1/2`). The inner div handles the entrance ANIMATION
    // (`abc-dropdown-anim` uses translateY which would otherwise clobber the
    // X translation if applied on the same element).
    <div
      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[760px] z-50"
      data-testid="services-mega"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="abc-dropdown-anim bg-white rounded-2xl shadow-[0_24px_60px_rgba(26,29,33,0.18)] border border-slate-100 p-7 grid grid-cols-3 gap-6">
        <div className="col-span-2 grid grid-cols-2 gap-x-6 gap-y-6">
          {SERVICE_CATEGORIES.map((cat) => (
            <div key={cat}>
              <div className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#E31D2B] mb-3">{cat}</div>
              <ul className="space-y-2">
                {SERVICES_BY_CATEGORY[cat].map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}`}
                      className="block text-[15px] text-[#191C20] hover:text-[#E31D2B] transition-colors leading-snug"
                      data-testid={`mega-service-${s.slug}`}
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#1A1D21] to-[#262A30] text-white p-5 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[#E31D2B] text-[11px] font-bold uppercase tracking-[0.16em] mb-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Featured Service
            </div>
            <div className="font-display font-extrabold text-xl leading-snug">Drain Cleaning</div>
            <p className="text-white/70 text-[13px] mt-2 leading-relaxed">
              Slow or clogged drains cleared fast — for homes across Baltimore and the surrounding metro.
            </p>
          </div>
          <div className="space-y-2 mt-5">
            <a
              href={BUSINESS.phoneHref}
              className="abc-btn abc-btn-accent w-full justify-center whitespace-nowrap"
              data-testid="mega-call-cta"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <QuoteCTA
              className="abc-btn abc-btn-ghost-light w-full justify-center whitespace-nowrap"
              data-testid="mega-quote-cta"
            >
              Get a Free Quote
            </QuoteCTA>
          </div>
        </div>
      </div>
    </div>
  );
}

function ServicesMegaMenu() {
  const { open, handleEnter, handleLeave } = useHoverDropdown();
  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={`px-3 py-2 text-sm font-semibold inline-flex items-center gap-1 transition-colors ${open ? "text-[#54616E]" : "text-[#191C20] hover:text-[#54616E]"}`}
        data-testid="nav-services-trigger"
        aria-expanded={open}
        aria-haspopup="true"
      >
        Services <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <MegaPanel onEnter={handleEnter} onLeave={handleLeave} />}
    </div>
  );
}

function AboutDropdown() {
  const { open, handleEnter, handleLeave } = useHoverDropdown();
  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={`px-3 py-2 text-sm font-semibold inline-flex items-center gap-1 transition-colors ${open ? "text-[#54616E]" : "text-[#191C20] hover:text-[#54616E]"}`}
        data-testid="nav-about-trigger"
        aria-expanded={open}
        aria-haspopup="true"
      >
        About <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-56 z-50"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="abc-dropdown-anim bg-white rounded-2xl shadow-[0_24px_60px_rgba(26,29,33,0.18)] border border-slate-100 p-2">
            <Link to="/about" className="block px-3 py-2.5 rounded-lg text-[15px] font-medium text-[#191C20] hover:bg-[#EDEFF2] hover:text-[#E31D2B] transition-colors" data-testid="nav-about-link">About Us</Link>
            <Link to="/gallery" className="block px-3 py-2.5 rounded-lg text-[15px] font-medium text-[#191C20] hover:bg-[#EDEFF2] hover:text-[#E31D2B] transition-colors" data-testid="nav-gallery-link">Gallery</Link>
            <Link to="/service-areas" className="block px-3 py-2.5 rounded-lg text-[15px] font-medium text-[#191C20] hover:bg-[#EDEFF2] hover:text-[#E31D2B] transition-colors" data-testid="nav-areas-link">Service Areas</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
      <ServicesMegaMenu />
      <AboutDropdown />
      {NAV_LINKS.map((link) => (
        <NavLink key={link.to} to={link.to} data-testid={link.testid}
          className={({ isActive }) =>
            `px-3 py-2 text-sm font-semibold transition ${isActive ? "text-[#54616E]" : "text-[#191C20] hover:text-[#54616E]"}`
          }>
          {link.label}
        </NavLink>
      ))}
      <a href={BUSINESS.phoneHref} className="ml-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1D21] hover:text-[#54616E] whitespace-nowrap" data-testid="header-call-link">
        <Phone className="w-4 h-4 text-[#54616E]" /> {BUSINESS.phone}
      </a>
      <QuoteCTA className="ml-3 abc-btn abc-btn-accent whitespace-nowrap" data-testid="header-quote-cta">
        Get a Free Quote
      </QuoteCTA>
    </nav>
  );
}

function MobileMenu() {
  return (
    <div className="xl:hidden bg-white border-t border-slate-100" data-testid="mobile-nav-menu">
      <div className="abc-container py-4 space-y-1">
        {MOBILE_NAV_ITEMS.map((l) => (
          <Link key={l.to} to={l.to} data-testid={l.testid}
            className="block px-3 py-3 rounded-xl text-base font-semibold text-[#1A1D21] hover:bg-[#EDEFF2]">
            {l.label}
          </Link>
        ))}
        <div className="pt-2 grid grid-cols-2 gap-2">
          <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-outline justify-center" data-testid="mnav-call">
            <Phone className="w-4 h-4" /> Call Now
          </a>
          <QuoteCTA className="abc-btn abc-btn-accent justify-center" data-testid="mnav-quote">
            Free Quote
          </QuoteCTA>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const closeMobileMenu = useCallback(() => setOpen(false), []);
  useCloseOnRouteChange(closeMobileMenu);

  return (
    <header className="sticky top-0 z-50 abc-glass border-b border-white/40 shadow-[0_2px_20px_rgba(26,29,33,0.04)]" data-testid="site-header">
      <div className="abc-container flex items-center justify-between h-16 xl:h-18">
        <Logo />
        <DesktopNav />
        <button
          className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 text-[#1A1D21]"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-nav-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && <MobileMenu />}
    </header>
  );
}
